import { Dispatch, SetStateAction, useEffect, useRef } from "react"
import { isActionColumn } from "../app-table/actionColumn"
import { LeafColumn } from "./types"

/**
 * Starts the action column frozen, beside the identity column a table freezes by default.
 *
 * A wide table moves its action next to the identity column so it cannot be scrolled out of
 * sight, and a column that scrolls away as soon as the table moves defeats that. Only done
 * where the table freezes something by default -- otherwise there is no run for the action
 * to join -- and only once, when the column first appears. After that, what is frozen is the
 * reader's choice and is left alone.
 *
 * Shared by the two tables that let a reader choose what to freeze, `EnhancedReportTable` and
 * `DataTable`, which keep the same frozen-keys state.
 */
export const useSeedActionFreeze = (
  leaves: LeafColumn[],
  defaultFreezeBoundaryKey: string | undefined,
  setFrozenColumnKeys: Dispatch<SetStateAction<string[]>>
) => {
  const seeded = useRef(false)

  useEffect(() => {
    if (seeded.current || !defaultFreezeBoundaryKey) return

    const actionLeaf = leaves.find(
      (leaf) =>
        !leaf.groupKey &&
        isActionColumn(leaf.column) &&
        (leaf.column as any).actionPlacement !== "end"
    )
    if (!actionLeaf) return

    seeded.current = true
    setFrozenColumnKeys((current) =>
      current.includes(actionLeaf.key) ? current : [...current, actionLeaf.key]
    )
  }, [leaves, defaultFreezeBoundaryKey, setFrozenColumnKeys])
}
